/* The video half of Detail: one <video>, its controls, and the reasons it might
   not play.
 *
 * The behaviour here is v1's (app/static/app.js's player), ported rather than
 * redesigned. That code is the product of a long argument with Instagram's CDN
 * and every apparent oddity in it is load-bearing:
 *
 * - **Resolve before assigning.** `?resolve=1` returns JSON instead of a 307 so
 *   the audio state can be read BEFORE the URL reaches `src`. Some reels have no
 *   audio track at all; without this they play silently with a live-looking
 *   volume control, which reads as a bug in the app.
 * - **Silent and soundless are different.** `silent` is the reel (nothing to
 *   unmute, so the control retires); `unavailable` is this resolve (Instagram
 *   strips audio from the formats it offers datacenter IPs), so the sound is
 *   really there and we did not get it. Both are readouts: ADR 0042 measured
 *   that re-asking the same egress never changes the answer.
 * - **A rejected `play()` is normal, not an error.** Mobile browsers expire the
 *   opening tap's user activation while yt-dlp resolves the URL, so autoplay is
 *   refused for a reason that has nothing to do with the video. The answer is to
 *   offer a fresh tap, not an error message.
 * - **An `error` event with no `video.error` attached is not a failure.** Seeks
 *   and brief stalls fire one. Flashing the fallback at those made the player
 *   look broken while it was working.
 * - **Retry once, then classify.** Anything the server does not name is very
 *   often a transient CDN hiccup, so the first one buys a cache-busted retry.
 *   Only a named reason — or a second failure — is worth showing a person.
 *
 * The element loops (as v1 does) and never advances by itself: moving through a
 * playlist is the reader's decision, not the player's (§30).
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { apiUrl, resolveVideo, videoUrl, type AudioState, type VideoFailure } from '../../core/api.js';
import { shareLink } from '../../core/share-link.js';
import { fmtDuration, isStillsItem } from '../../../../packages/core/index.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';

/** What the user is told, per reason the server gives. */
const MESSAGES: Record<Exclude<VideoFailure, ''>, string> = {
  restricted: 'Instagram is restricting this video. It still plays at the source.',
  deleted: 'The owner deleted this video. Everything below was saved before it went.',
  rate_limited: 'Instagram is rate-limiting us right now. Try again in a few minutes.',
  dash_only: 'This one streams audio and video separately, which the in-page player can’t combine.',
};

/** An image post, which the web does not page through (ADR 0029 A3.1, 0049).
 *  It says where the pictures can be seen instead — owner's wording, app
 *  mention kept. */
const STILLS_MESSAGE = 'This post is pictures. See them all in the Grain app, or at the source.';

@customElement('player-surface')
export class PlayerSurface extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: block;
        position: relative;
        /* Black, not --bg: a 9:16 video in a 16:9 pane always has bars, and the
           bars belong to the video rather than to the page around it. This is
           the one surface in v2 that is not theme-coloured, in both themes. */
        background: #000;
        overflow: hidden;
      }
      .stage {
        position: relative;
        width: 100%;
        height: 100%;
      }
      video {
        display: block;
        width: 100%;
        height: 100%;
        /* contain, never cover: cropping a reel cuts off the on-screen text that
           is frequently the whole point of the save. */
        object-fit: contain;
      }
      video[hidden] {
        display: none;
      }
      /* An image post's first picture (ADR 0049): letterboxed in the same frame
         as a video, so nothing about the frame changes between the two. */
      .still {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      /* The message sits over a PHOTOGRAPH here, not over black as a playback
         failure does, so it carries its own ground. A panel, not a capsule:
         a capsule means tappable (ADR 0013), and the message is not. */
      .failure.over-still {
        border-radius: var(--radius-md);
        background: rgb(0 0 0 / 0.62);
      }
      /* Legibility for the controls over an arbitrary bright frame. Both ends
         are scrimmed because the remaining-time pill sits at the top. */
      .scrim {
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        background: linear-gradient(rgb(0 0 0 / 0.45), transparent 22%, transparent 62%, rgb(0 0 0 / 0.72));
      }
      .remaining {
        position: absolute;
        top: var(--space-3);
        right: var(--space-3);
        z-index: 3;
        padding: 3px 9px;
        border-radius: var(--radius-full);
        background: rgb(0 0 0 / 0.55);
        color: #fff;
        font-family: var(--font-mono);
        font-size: 0.72rem;
      }
      /* Bottom-right, stacked, clearing the progress bar's 20px hit area. What
         is left here is *how* it plays — whether it plays is the frame itself
         (click to toggle), so there is no play button in this column.

         Order is share, speed, audio, and the audio control being LAST is
         load-bearing rather than cosmetic: the column is anchored at the bottom,
         so the last child is the one pinned there, and stripping the two above
         it when a sheet opens moves it by exactly nothing. */
      .controls {
        position: absolute;
        right: var(--space-3);
        bottom: 26px;
        z-index: 3;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: var(--space-2);
      }
      /* Fixed colours rather than tokens, for the same reason as the black
         background: these sit on video frames, not on a themed surface. */
      .ctrl {
        position: relative;
        display: grid;
        place-items: center;
        width: 34px;
        height: 34px;
        padding: 0;
        border: 0;
        border-radius: var(--radius-full);
        background: rgb(0 0 0 / 0.55);
        color: #fff;
        font-family: var(--font-mono);
        font-size: 0.72rem;
        font-weight: 600;
        cursor: pointer;
        --icon-size: 16px;
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips). */
      @media (hover: hover) {
        .ctrl:hover {
          background: rgb(0 0 0 / 0.75);
        }
      }
      .ctrl:active {
        background: rgb(0 0 0 / 0.88);
      }
      /* A 20px pointer target for a 2.5px line — the bar is thin because it is
         ambient, not because it should be hard to hit. */
      .progress {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 3;
        display: flex;
        align-items: flex-end;
        height: 20px;
        cursor: pointer;
        touch-action: none;
      }
      .track {
        width: 100%;
        height: 2.5px;
        background: rgb(255 255 255 / 0.25);
        transition: height 140ms ease;
      }
      /* Split, not wrapped: .scrubbing is a real state driven by the drag and has
         to work on touch — only the hover half is guarded, or the bar stays fat
         after a tap. */
      .progress.scrubbing .track {
        height: 5px;
      }
      @media (hover: hover) {
        .progress:hover .track {
          height: 5px;
        }
      }
      .fill {
        width: 0;
        height: 100%;
        background: #fff;
      }
      .middle {
        position: absolute;
        inset: 0;
        z-index: 2;
        display: grid;
        place-items: center;
      }
      .tap {
        display: grid;
        place-items: center;
        width: 72px;
        height: 72px;
        /* The triangle's own mass sits right of its box centre, so centring the
           box leaves it looking left-of-centre in a circle. */
        padding-left: 5px;
        border: 0;
        border-radius: var(--radius-full);
        background: var(--accent);
        color: var(--accent-ink);
        cursor: pointer;
        --icon-size: 30px;
      }
      /* Dims rather than washes: it is the one in-air control that owns an opaque
         accent fill, so ink over it would barely move. */
      .tap:active {
        filter: brightness(0.9);
      }
      /* Paused has to look paused. With no play/pause button, a stopped video is
         otherwise indistinguishable from a frozen one — this is the only sign
         the frame is a toggle. Deliberately quieter than the .tap button, the
         accent-coloured "autoplay was refused, ask again" button: these mean
         different things and must not look alike. It ignores the pointer so the
         click lands on the frame underneath. */
      .glyph {
        display: grid;
        place-items: center;
        width: 58px;
        height: 58px;
        padding-left: 4px;
        border-radius: var(--radius-full);
        background: rgb(0 0 0 / 0.45);
        color: rgb(255 255 255 / 0.9);
        pointer-events: none;
        --icon-size: 24px;
      }
      .spinner {
        width: 32px;
        height: 32px;
        border: 3px solid rgb(255 255 255 / 0.25);
        border-top-color: #fff;
        border-radius: var(--radius-full);
        animation: spin 0.9s linear infinite;
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .spinner {
          animation-duration: 2.4s;
        }
      }
      .failure {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-3);
        max-width: 34ch;
        padding: var(--space-5);
        text-align: center;
      }
      .failure p {
        margin: 0;
        color: rgb(255 255 255 / 0.82);
        font-size: 0.88rem;
        line-height: 1.5;
      }
      .source {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        background: var(--accent);
        color: var(--accent-ink);
        font-size: 0.85rem;
        font-weight: 600;
        text-decoration: none;
        --icon-size: 14px;
      }
      /* Takes the mute control's slot rather than annotating it. An earlier
         version kept the button rendered but inert with this pill above it, on
         the grounds that removing it would read as "we forgot one" — which held
         while the controls were a fixed column on a desktop slab, and stopped
         holding when they became two capsules floating on a phone frame. A
         control that cannot be operated is not worth a 44px target, and the
         pill carries the whole fact on its own: a silent video with no
         explanation reads as a bug in the app.

         Two pills share these styles, for the two reasons there is no sound,
         and BOTH are readouts. "no audio" is the reel: silent at the source.
         "audio unavailable" is this resolve: Instagram withheld the audio from
         the address that asked, so the sound exists and we did not get it —
         worth saying, and different from declaring the reel mute.

         Neither is tappable, which is ADR 0013's rule holding rather than
         bending: a capsule means tappable unless it plainly reads as a label.
         The second one WAS a retry button until 0042 measured what a retry
         does — the withholding is per reel and deterministic, so re-asking the
         same egress returned the same answer 15 times out of 15. It offered
         something it could never deliver, and each tap was another request
         from the address that was already distrusted. */
      .muted-note {
        display: grid;
        place-items: center;
        min-height: 34px;
        padding: 3px 9px;
        border-radius: var(--radius-full);
        background: rgb(0 0 0 / 0.55);
        color: rgb(255 255 255 / 0.8);
        font-family: var(--font-mono);
        font-size: 0.68rem;
        white-space: nowrap;
      }
      /* ── Phone: in-air controls over a full-bleed frame ──────────────
         No bars. The scrim above already exists to keep controls legible over
         an arbitrary frame, so everything floats on it and the video keeps the
         whole screen — a band across the top and bottom would draw two
         horizontals through the reel and make it feel smaller than it is.

         36px of paint, 44px of hit area, exactly as g-chips resolved it:
         Material's own controls do not grow on touch, they extend invisibly.
         The gap stays at --space-3 so neighbouring extensions cannot overlap.

         --overhang is the part of a fixed overlay that falls past the visible
         viewport — the system navigation bar. env(safe-area-inset-bottom)
         cannot be used for this: it reports 0 there, because a nav bar is not a
         display cutout (ADR 0010 §5). g-detail measures it and sets it. */
      @media (max-width: 959px) {
        .remaining {
          top: calc(var(--space-3) + var(--safe-t, 0px));
        }
        /* --ctrl-lift is the caller's: g-detail parks the title and the Detail
           button in a band of their own between the progress bar and this
           column, and only it knows how tall that band is. */
        .controls {
          bottom: calc(26px + var(--overhang, 0px) + var(--ctrl-lift, 0px));
          gap: var(--space-3);
        }
        .ctrl {
          width: 36px;
          height: 36px;
          --icon-size: 18px;
        }
        .ctrl::after {
          content: '';
          position: absolute;
          inset: -4px;
        }
        .muted-note {
          min-height: 36px;
        }
        .progress {
          bottom: var(--overhang, 0px);
          height: 24px;
        }
      }
    `,
  ];

  /** Instagram shortcode — the key /api/video resolves. Changing it reloads. */
  @property() shortcode = '';
  /** Source URL, for the "open at the source" fallback. */
  @property() url = '';
  /**
   * The item's saved thumbnail, shown while the CDN URL is being resolved.
   *
   * Resolving costs a yt-dlp call, so the frame would otherwise be black for a
   * second or more behind a spinner. The thumbnail arrives with the detail
   * fetch, which is much faster, and it letterboxes exactly as the video will
   * (`object-fit: contain` covers the poster too) so nothing shifts when the
   * first real frame paints. Empty for the ~4% whose thumbnail download failed.
   *
   * Carries the **payload path** (`/thumbs/<code>.jpg`), not a URL: this
   * component runs it through `apiUrl()` at the binding, next to the `videoUrl`
   * that builds `<video src>`, so both of the element's media URLs stay
   * impersonation-aware in one place. Handing it an already-absolute URL still
   * works — `new URL(absolute, origin)` is a no-op — but a caller that builds
   * the URL itself is a caller that can forget to. ADR 0024.
   */
  @property() poster = '';
  /** The item's title, for the share sheet. Not `title`, which HTMLElement owns. */
  @property() shareTitle = '';
  /**
   * Strip everything but the audio control.
   *
   * Set while a sheet is over the player: speed, share, the progress bar, the
   * remaining time and the scrim all have no job while the reader is reading,
   * and the audio control does — killing the sound to read a transcript is a
   * real thing people do. It is the last child of the column, so the others
   * leaving moves it by nothing (ADR 0013).
   */
  @property({ type: Boolean }) chromeless = false;
  /**
   * What the item is (ADR 0029). An image post (isStillsItem) makes NO
   * /api/video request at all — no resolve, no 307, no retry — and shows its
   * first picture with a note saying where to see the rest (ADR 0049). Until
   * then it made five requests and ended on a false "rate-limiting" message
   * over a black frame.
   */
  @property() mediaType = '';

  @state() private failure: VideoFailure | null = null;
  /** The current item is an image post, shown as its first picture. */
  @state() private stills = false;
  /** The share fell back to the clipboard; say so on the button for a moment. */
  @state() private copied = false;
  @state() private buffering = true;
  /** Autoplay was refused; the user has to ask again. */
  @state() private needsTap = false;
  @state() private paused = false;
  @state() private muted = false;
  /**
   * What the server knows about this reel's sound. `silent` retires the mute
   * control (there is nothing to unmute); `unavailable` replaces it with a
   * retry, because the sound exists and only this resolve missed it — see
   * `AudioState` in core/api.ts.
   */
  @state() private audio: AudioState = 'present';
  /** A retry is in flight; the capsule says so rather than looking ignored. */
  @state() private rate = 1;
  @state() private progress = 0;
  @state() private remaining = '';
  @state() private scrubbing = false;

  @query('video') private video?: HTMLVideoElement;

  /** One per load; a resolve that lands after the item changed is dropped. */
  private token = 0;
  private retried = false;

  override updated(changed: Map<string, unknown>): void {
    /* Both, because the two arrive together from one detail: reloading on the
       shortcode alone would decide stills-or-video from the previous item's
       type whenever the type changed in the same render. One load either way —
       Lit hands both changes to a single updated(). */
    if (changed.has('shortcode') || changed.has('mediaType')) void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    // Detaching the source matters: a <video> removed from the DOM with a live
    // src keeps the connection and the decoder alive.
    this.token += 1;
    const v = this.video;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
  }

  private async load(): Promise<void> {
    const mine = ++this.token;
    this.failure = null;
    this.buffering = true;
    this.needsTap = false;
    this.audio = 'present';
    this.muted = false;
    this.rate = 1;
    this.progress = 0;
    this.remaining = '';
    this.paused = false;
    this.retried = false;

    // Detach the previous item's source before resolving the next one. The
    // element is reused across the playlist, so without this its last painted
    // frame stays on screen for the whole resolve — which is precisely the gap
    // the poster exists to fill, and it would show the WRONG item meanwhile.
    // Clearing the source returns the element to its poster; it does not set
    // `video.error`, so the error path is unaffected.
    const v = this.video;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    this.stills = isStillsItem({ media_type: this.mediaType });
    if (this.stills) {
      // Nothing to wait for: the picture is the poster, already in hand.
      this.buffering = false;
      return;
    }
    if (!this.shortcode) return;

    const outcome = await resolveVideo(this.shortcode);
    if (mine !== this.token) return;

    if (outcome.ok) {
      this.audio = outcome.audio;
      if (outcome.audio !== 'present') this.muted = true;
      this.play(outcome.url);
      return;
    }
    if (outcome.reason) {
      this.showFailure(outcome.reason);
      return;
    }
    // Unclassified: let the element follow the 307 itself and report through
    // its own error event, which is where the retry lives.
    this.play(videoUrl(this.shortcode));
  }

  private play(src: string): void {
    const v = this.video;
    if (!v) return;
    v.muted = this.muted;
    v.playbackRate = this.rate;
    v.src = src;
    v.load();
    this.tryPlay();
  }

  private tryPlay(): void {
    const v = this.video;
    if (!v) return;
    v.play().then(
      () => {
        this.needsTap = false;
        this.paused = false;
      },
      () => {
        // Only offer the tap if nothing worse is going on — an actual media
        // error has its own treatment and must not be masked by a play button.
        if (!v.error && !this.failure) {
          this.buffering = false;
          this.needsTap = true;
        }
      },
    );
  }

  private showFailure(reason: VideoFailure): void {
    this.failure = reason || 'rate_limited';
    this.buffering = false;
    this.needsTap = false;
  }

  private async onError(): Promise<void> {
    const v = this.video;
    // No MediaError means a seek or a stall, not a failure.
    if (!v?.error || !this.shortcode) return;
    const mine = this.token;

    const outcome = await resolveVideo(this.shortcode);
    if (mine !== this.token) return;
    const reason = outcome.ok ? '' : outcome.reason;

    if (!reason && !this.retried) {
      this.retried = true;
      this.buffering = true;
      this.play(videoUrl(this.shortcode, Date.now()));
      return;
    }
    this.showFailure(reason);
  }

  private onTimeUpdate(): void {
    const v = this.video;
    if (!v?.duration) return;
    this.progress = (v.currentTime / v.duration) * 100;
    this.remaining = fmtDuration(v.duration - v.currentTime);
  }

  private toggleMute(): void {
    const v = this.video;
    if (!v || this.audio !== 'present') return;
    this.muted = !this.muted;
    v.muted = this.muted;
  }

  /**
   * Ask for the sound again, keeping the frame where it is.
   *
   * `refresh` is what makes this more than a no-op: the soundless URL is in the
   * server's cache, and a plain re-resolve would hand back the same one. Only a
   * resolve that comes back with audio disturbs playback (the element has to take
   * the new URL); a second silent answer leaves the video running and says so,
   * because interrupting a reel to report a failure is a worse trade than the
   * silence the user already has.
   */

  private toggleRate(): void {
    const v = this.video;
    if (!v) return;
    this.rate = this.rate === 2 ? 1 : 2;
    v.playbackRate = this.rate;
  }

  /**
   * Stop playing, from outside.
   *
   * A method and not a property, deliberately: opening the full sheet pauses,
   * and closing it does NOT resume. A `?paused` property would imply that
   * symmetry and invite someone to restore it later — the asymmetry is the
   * decision (ADR 0013). Coming back to a paused frame is self-explaining,
   * because the centred glyph already says the frame is a toggle.
   */
  pause(): void {
    this.video?.pause();
  }

  private async onShare(): Promise<void> {
    if (!this.url) return;
    const outcome = await shareLink({ title: this.shareTitle || undefined, url: this.url });
    if (outcome !== 'copied') return;
    this.copied = true;
    setTimeout(() => (this.copied = false), 1600);
  }

  private togglePlay(): void {
    const v = this.video;
    if (!v) return;
    if (v.paused) void v.play().catch(() => {});
    else v.pause();
  }

  /** Click the frame itself — anywhere the controls aren't. */
  private onStageClick(e: MouseEvent): void {
    const el = e.target as HTMLElement;
    if (el.closest('button, a, .progress')) return;
    // A still has nothing to play, and togglePlay on an empty element would be
    // refused and raise the tap-to-play button over a photograph.
    if (this.stills) return;
    this.togglePlay();
  }

  private seek(e: PointerEvent): void {
    const v = this.video;
    const bar = e.currentTarget as HTMLElement;
    if (!v?.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    v.currentTime = ratio * v.duration;
    this.progress = ratio * 100;
    this.remaining = fmtDuration(v.duration - v.currentTime);
  }

  private renderMiddle() {
    // Under a sheet the frame is a sliver of context, not a control. A 58px
    // paused glyph inside a 57px strip reads as a play button, and tapping it
    // steps the sheet down rather than playing — so it would be a lie. The
    // spinner survives because it explains a wait rather than offering an
    // action, and a failure message is unreadable at that height anyway.
    if (this.chromeless) {
      return this.buffering ? html`<div class="spinner" role="status" aria-label="Loading"></div>` : nothing;
    }
    if (this.stills) {
      return html`<div class="failure over-still">
        <p>${STILLS_MESSAGE}</p>
        ${this.url
          ? html`<a class="source" href=${this.url} target="_blank" rel="noopener">
              <g-icon name="external"></g-icon>Open at the source
            </a>`
          : nothing}
      </div>`;
    }
    if (this.failure) {
      return html`<div class="failure">
        <p>${MESSAGES[this.failure as Exclude<VideoFailure, ''>] ?? MESSAGES.rate_limited}</p>
        ${this.failure === 'deleted' || !this.url
          ? nothing
          : html`<a class="source" href=${this.url} target="_blank" rel="noopener">
              <g-icon name="external"></g-icon>Open at the source
            </a>`}
      </div>`;
    }
    if (this.needsTap) {
      return html`<button class="tap" aria-label="Play" @click=${() => this.tryPlay()}>
        <g-icon name="play"></g-icon>
      </button>`;
    }
    if (this.buffering) return html`<div class="spinner" role="status" aria-label="Loading"></div>`;
    if (this.paused) {
      return html`<div class="glyph"><g-icon name="play"></g-icon></div>`;
    }
    return nothing;
  }

  override render() {
    const middle = this.renderMiddle();
    const playing = !this.failure && !this.needsTap && !this.stills;
    return html`
      <div class="stage" @click=${this.onStageClick}>
        <video
          playsinline
          loop
          preload="auto"
          poster=${this.poster ? apiUrl(this.poster) : nothing}
          ?hidden=${this.failure !== null || this.stills}
          @waiting=${() => {
            if (!this.needsTap) this.buffering = true;
          }}
          @playing=${() => {
            // Reaching 'playing' means it works — clear anything a transient
            // blip put on screen.
            this.buffering = false;
            this.needsTap = false;
            this.failure = null;
            this.paused = false;
          }}
          @pause=${() => (this.paused = true)}
          @play=${() => (this.paused = false)}
          @canplay=${() => {
            this.buffering = false;
            if (this.video?.paused && !this.needsTap && !this.failure) this.tryPlay();
          }}
          @timeupdate=${this.onTimeUpdate}
          @error=${() => void this.onError()}
        ></video>

        ${this.stills && this.poster
          ? html`<img class="still" src=${apiUrl(this.poster)} alt="" />`
          : nothing}
        ${playing && !this.chromeless ? html`<div class="scrim"></div>` : nothing}
        ${middle === nothing ? nothing : html`<div class="middle">${middle}</div>`}
        ${playing && !this.chromeless && this.remaining
          ? html`<div class="remaining">${this.remaining}</div>`
          : nothing}
        ${playing
          ? html`
              <div class="controls">
                ${this.chromeless
                  ? nothing
                  : html`
                      ${this.url
                        ? html`<button
                            class="ctrl"
                            aria-label=${this.copied ? 'Link copied' : 'Share'}
                            @click=${() => void this.onShare()}
                          >
                            <g-icon name=${this.copied ? 'check' : 'share'}></g-icon>
                          </button>`
                        : nothing}
                      <button class="ctrl" aria-label="Playback speed" @click=${this.toggleRate}>
                        ${this.rate}×
                      </button>
                    `}
                ${this.audio === 'silent'
                  ? html`<div class="muted-note">no audio</div>`
                  : this.audio === 'unavailable'
                    ? html`<div class="muted-note">audio unavailable</div>`
                    : html`<button
                        class="ctrl"
                        aria-label=${this.muted ? 'Unmute' : 'Mute'}
                        @click=${this.toggleMute}
                      >
                        <g-icon name=${this.muted ? 'mute' : 'vol'}></g-icon>
                      </button>`}
              </div>
              ${this.chromeless ? nothing : html`<div
                class="progress ${this.scrubbing ? 'scrubbing' : ''}"
                @pointerdown=${(e: PointerEvent) => {
                  this.scrubbing = true;
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  this.seek(e);
                }}
                @pointermove=${(e: PointerEvent) => {
                  if (this.scrubbing) this.seek(e);
                }}
                @pointerup=${() => (this.scrubbing = false)}
                @pointercancel=${() => (this.scrubbing = false)}
              >
                <div class="track"><div class="fill" style="width:${this.progress}%"></div></div>
              </div>`}
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'player-surface': PlayerSurface;
  }
}
